import { memo } from 'react';
import { Link } from 'react-router-dom';
import {
  Grid, Typography, Fab, Stack,
  Container,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Configure, InstantSearch } from 'react-instantsearch-hooks-web';
import { useIsMobile } from '@client/hooks/dom.hooks';
import { algoliaClient } from '@client/algolia';
import { getShowPath } from '@sda/utils/url';
import { getAlgoliaIndexName, serializeFilters } from '@sda/utils/algolia';
import { AlgoliaIndices } from '@sda/enums/algolia';
import { Hits } from '@client/algolia/InstantSearch/Hits';
import { getEventLocation } from '@sda/utils/string';
import { getDateTime } from '@sda/utils/date';
import { ShowCard } from '../../../common/BaseCard/ShowCard';
import type { AlgoliaShow } from '@sda/helpers/algolia.helpers';
import type { AlgoliaFilter } from '@sda/utils/algolia/interfaces';

const useStyles = makeStyles()({
  fab: {
    whiteSpace: 'nowrap',
  },
});

const defaultFilters = {
  'publicShow.isIncomplete': {
    type: 'boolean',
    value: false,
  },
  'publicShow.isCancelled': {
    type: 'boolean',
    value: false,
  },
  'publicShow.endTime._seconds': {
    type: 'futureDate',
  },
  'publicShow.hasPublicTickets': {
    type: 'boolean',
    value: true,
  },
  'publicShow.hasExternalTickets': {
    type: 'boolean',
    value: true,
    not: true,
  },
  'publicShow.announceTime._seconds': {
    type: 'pastDate',
  },
};

const attributesToRetrieve = [
  'publicShow.publicImageUrl',
  'publicShow.title',
  'publicShow.capacity',
  'publicShow.city',
  'publicShow.region',
  'publicShow.startTime',
  'publicShow.timezoneOverride',
  'publicShow.currencyOverride',
];

export const Shows = memo(() => {
  const { classes } = useStyles();
  const isMobile = useIsMobile();

  return (
    <Container maxWidth="md">
      <Stack spacing={3}>
        <div>
          <Grid
            container
            spacing={isMobile ? 0 : 3}
            alignItems="center"
            justifyContent={isMobile ? 'space-between' : 'flex-start'}
            flexWrap="nowrap"
          >
            <Grid item>
              <Typography
                variant={isMobile ? 'h6' : 'h4'}
                noWrap
              >
                Upcoming Shows
              </Typography>
            </Grid>

            <Grid item>
              <Fab
                color="info"
                component={Link}
                to="/shows"
                className={classes.fab}
              >
                View More
              </Fab>
            </Grid>
          </Grid>
        </div>

        <InstantSearch
          searchClient={algoliaClient}
          indexName={getAlgoliaIndexName(AlgoliaIndices.SHOWS)}
        >
          <Configure
            filters={serializeFilters(defaultFilters as Record<string, AlgoliaFilter>)}
            hitsPerPage={3}
            attributesToRetrieve={attributesToRetrieve}
            attributesToHighlight={[]}
            aroundLatLngViaIP
            aroundPrecision={100000}
          />

          <div>
            <Hits<any, AlgoliaShow>
              component={Grid}
              container
              spacing={3}
              hitComponent={({ hit }) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                >
                  <ShowCard
                    showId={hit.objectID}
                    currency={hit.publicShow.currencyOverride}
                    imgSrc={hit.publicShow.publicImageUrl}
                    path={getShowPath(hit.objectID)}
                    title={hit.publicShow.title}
                    location={getEventLocation(hit.publicShow)}
                    startTime={getDateTime(hit.publicShow.startTime).setZone(hit.publicShow.timezoneOverride).toFormat('LLLL d, h:mm a')}
                  />
                </Grid>
              )}
            />
          </div>
        </InstantSearch>
      </Stack>
    </Container>
  );
});
